import { ref, watch } from 'vue'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { initializeApp, getApps } from 'firebase/app'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'
import { jsonapiClient } from '@/api/client'
import { usePlatform } from '@/composables/usePlatform'
import { useAuthStore } from '@/stores/auth'
import { useOrganizationStore } from '@/stores/organization'
import { useOrganizationSwitch } from '@/composables/useOrganizationSwitch'
import { isShellReady } from '@/composables/useShellReady'
import { isRealRoute } from '@/router/notFound'

type PushPlatform = 'ios' | 'android' | 'web'
const isPushPlatform = (p: string): p is PushPlatform =>
  p === 'ios' || p === 'android' || p === 'web'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_WEB_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_WEB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_WEB_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_WEB_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_WEB_APP_ID,
}

// FCM `data` arrives as an untyped string map. Narrow it once so callers can read
// fields without casts.
function asStringRecord(data: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  if (data && typeof data === 'object') {
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') out[key] = value
    }
  }
  return out
}

// A deep link is a path AND the tenant it belongs to. The server stamps
// `organization_id` on every payload (Notifications::Delivery); a payload from
// before that stamp has none and is followed under the active organization.
interface DeepLinkTarget {
  path: string
  organizationId?: string
}

function extractTarget(data: unknown): DeepLinkTarget | undefined {
  const record = asStringRecord(data)
  if (!record.path) return undefined
  return { path: record.path, organizationId: record.organization_id || undefined }
}

// Module-level singletons: listeners attach once for the app's lifetime, and a
// tapped-notification target survives until the shell + auth are ready to honor
// it (e.g. a cold launch, or a tap while signed out).
const pendingTarget = ref<DeepLinkTarget | null>(null)
let tapListenerAttached = false
let tokenListenerAttached = false
let foregroundListenerAttached = false
let swMessageListenerAttached = false
let swRegistration: ServiceWorkerRegistration | null = null

/**
 * Attach the notification-tap listener as early as possible — before the Vue app
 * mounts — so a cold launch from a tapped notification isn't missed. Deliberately
 * minimal: it only records the target job; navigation happens later once the
 * shell and auth are ready. Safe to call before Pinia/router exist (no store or
 * network access). No-op on web (the web SW deep-links directly).
 */
export async function primePushTapListener() {
  const { isNative } = usePlatform()
  if (!isNative || tapListenerAttached) return
  tapListenerAttached = true

  await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    const target = extractTarget(event.notification?.data)
    if (target) pendingTarget.value = target
  })
}

/** Initialize the Firebase JS app once (web getToken/getMessaging need it). */
function ensureFirebaseApp() {
  if (!getApps().length) initializeApp(firebaseConfig)
}

/**
 * Register our own service worker with the Firebase config in the query string,
 * so firebase-messaging-sw.js can initialize for background push. We must do this
 * (and pass the registration to getToken) because Firebase otherwise auto-registers
 * the SW WITHOUT the query string, leaving it config-less. Registered once.
 */
async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (swRegistration) return swRegistration
  const params = new URLSearchParams(firebaseConfig)
  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${params.toString()}`,
  )
  // getToken() -> PushManager.subscribe() requires an ACTIVE worker. A freshly
  // registered SW is still installing/activating, so on a first load
  // registration.active is null -> "no active Service Worker" AbortError. Wait for
  // this specific worker to reach 'activated' before handing it to getToken.
  await workerActivated(registration)
  swRegistration = registration
  return swRegistration
}

/** Resolves once the registration has an activated worker. */
function workerActivated(reg: ServiceWorkerRegistration): Promise<void> {
  if (reg.active) return Promise.resolve()
  const worker = reg.installing ?? reg.waiting
  if (!worker) return navigator.serviceWorker.ready.then(() => undefined)
  return new Promise((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve()
    })
  })
}

/**
 * Push notification wiring for both native (Capacitor) and web (Console browser).
 * Create one instance in the app shell: call init() once (attaches listeners +
 * registers the token + honors any pending deep-link), and register() again
 * whenever the signed-in user changes so the token is tied to the right account.
 */
export function usePushNotifications() {
  const { isNative, platform } = usePlatform()
  const router = useRouter()
  const auth = useAuthStore()
  const orgStore = useOrganizationStore()
  const { switchOrganization } = useOrganizationSwitch()
  const { t } = useI18n()

  // Follow a push deep link, but only if its path resolves to a real route:
  // this is both the drift guard (a stale backend path no-ops instead of 404-ing)
  // and the open-redirect guard. No-op on anything that isn't an in-app path.
  //
  // The link names its tenant. Another of the user's organizations ⇒ switch to
  // it AND land on the record in one flush (useOrganizationSwitch), because the
  // path read under the active organization would be a Silent Wall 404. An
  // organization that is not the user's is ignored like an unmatched route.
  //
  // No organization selected yet (a multi-org user after a logout or a first
  // install, parked on the picker): nothing tenant-bound is mounted, so the
  // one-flush dance is not needed — and would not work, since the picker's
  // guard bounces any tenant route until an organization is set. Select, then go.
  function openDeepLink({ path, organizationId }: DeepLinkTarget) {
    if (!path.startsWith('/')) return
    // A path this app does not have may still « resolve » to the catch-all
    // (the phone's redirects Home) — that is not a match either.
    if (!isRealRoute(router.resolve(path))) {
      if (import.meta.env.DEV) console.warn(`[push] ignoring notification path with no matching route: ${path}`)
      return
    }

    if (organizationId && organizationId !== orgStore.currentOrganizationId) {
      if (!orgStore.organizations.some(org => org.id === organizationId)) return
      if (orgStore.currentOrganizationId) {
        void switchOrganization(organizationId, path)
        return
      }
      orgStore.setCurrentOrganization(organizationId)
    }
    void router.push(path)
  }

  async function registerToken(token: string) {
    if (!token || !isPushPlatform(platform)) return
    try {
      await jsonapiClient.post('/api/me/devices', {
        data: { type: 'device' as const, attributes: { token, platform } },
      })
    } catch {
      // Best-effort — never block the app on token registration.
    }
  }

  /** Returns whether permission was granted, so callers can react if they wish. */
  async function register(): Promise<boolean> {
    if (isNative) {
      const { receive } = await FirebaseMessaging.requestPermissions()
      if (receive !== 'granted') return false
      const { token } = await FirebaseMessaging.getToken()
      await registerToken(token)
      return true
    }

    // Web: needs SW + Push API support and the firebase app initialized. Init the
    // app FIRST — the plugin's web constructor calls getMessaging() (which needs an
    // initialized app) the moment it's instantiated on this first plugin access.
    ensureFirebaseApp()
    const { isSupported } = await FirebaseMessaging.isSupported()
    if (!isSupported) return false

    const { receive } = await FirebaseMessaging.requestPermissions()
    if (receive !== 'granted') return false

    const registration = await ensureServiceWorker()
    const { token } = await FirebaseMessaging.getToken({
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
    await registerToken(token)
    return true
  }

  async function attachTokenListener() {
    if (tokenListenerAttached) return
    tokenListenerAttached = true

    // FCM rotates tokens; re-register whenever a new one arrives.
    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      void registerToken(event.token)
    })
  }

  // Foreground messages don't fire the SW's onBackgroundMessage, so surface them
  // as a toast that deep-links to the job on click. Web-only (native shows its
  // own system notification in the foreground).
  async function attachForegroundListener() {
    if (foregroundListenerAttached) return
    foregroundListenerAttached = true

    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      // Web messages are data-only, so title/body live in `data` (not in the
      // notification block); fall back to the notification fields just in case.
      const data = asStringRecord(event.notification?.data)
      const target = extractTarget(data)
      const title = data.title || event.notification?.title || t('push.notification.fallbackTitle')
      const body = data.body || event.notification?.body || undefined
      toast(title, {
        description: body,
        action: target
          ? { label: t('push.notification.view'), onClick: () => openDeepLink(target) }
          : undefined,
      })
    })
  }

  // The web SW posts a message when a background notification is clicked; route
  // the focused tab in place (no reload) instead of letting it hard-navigate.
  function attachSwMessageListener() {
    if (swMessageListenerAttached || !('serviceWorker' in navigator)) return
    swMessageListenerAttached = true

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type !== 'push-notification-click') return
      // The worker relays the payload's own keys (path, organization_id).
      const target = extractTarget(event.data)
      if (target) openDeepLink(target)
    })
  }

  // Honor a tapped notification once the shell is painted, the user is in AND
  // the organizations are loaded. `immediate` covers a cold launch where the
  // target is already set; the watch also holds it across a sign-in if the tap
  // happened while signed out. ⚠ The organizations gate is not decoration: a
  // sign-in flips `isAuthenticated` BEFORE it fetches the organizations, and a
  // target consumed in that gap finds an empty list and is dropped for good.
  // Native only — web routes directly via the SW message / openWindow URL.
  watch(
    () => [ isShellReady.value, auth.isAuthenticated, pendingTarget.value, orgStore.organizations.length > 0 ] as const,
    ([ ready, authed, target, orgsLoaded ]) => {
      if (ready && authed && target && orgsLoaded) {
        openDeepLink(target)
        pendingTarget.value = null
      }
    },
    { immediate: true },
  )

  async function init() {
    if (isNative) {
      await primePushTapListener()
      await attachTokenListener()
      await register()
      return
    }

    // Web. Init the Firebase app BEFORE any plugin call (see register() — the
    // plugin's web ctor calls getMessaging() on instantiation), then bail if push
    // isn't supported in this browser.
    ensureFirebaseApp()
    const { isSupported } = await FirebaseMessaging.isSupported()
    if (!isSupported) return

    attachSwMessageListener()
    await attachForegroundListener()
    // Registration (permission prompt + getToken) is deferred to register(),
    // invoked by the shell once the user is authenticated — we don't prompt
    // for notifications before login.
  }

  return { init, register }
}
